import { DeleteOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Select, Space } from 'antd';
import * as Component from 'components';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SERVICE_DISTRIBUTION_OPTIONS, STATUS_OPTIONS } from '../constants';
import { movieService } from '../services/movieService';
import { MovieListItem } from '../types/movieType';
import './Movie.css';

const Movie: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceDistributionFilter, setServiceDistributionFilter] = useState<string[]>(
    searchParams.get('service_distribution')?.split(',') || []
  );
  const [statusFilter, setStatusFilter] = useState<string | null>(searchParams.get('status'));

  const handleServiceDistributionChange = (values: string[]) => {
    setServiceDistributionFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('service_distribution', values.join(','));
    } else {
      newParams.delete('service_distribution');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('status', value);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // Get English poster thumbnails (landscape and portrait)
  const getEnglishPosters = (thumbnails?: any[]) => {
    if (!thumbnails) return { landscape: null, portrait: null };
    const englishThumbnails = thumbnails.filter(
      t => t.language?.toUpperCase() === 'EN' || t.language?.toUpperCase() === 'ENGLISH'
    );
    return {
      landscape: englishThumbnails.find(t => t.orientation === 'landscape'),
      portrait: englishThumbnails.find(t => t.orientation === 'portrait'),
    };
  };

  const columns = [
    {
      title: 'POSTER',
      dataIndex: 'thumbnails',
      key: 'poster',
      width: '15%',
      render: (_text: string, record: MovieListItem) => {
        const posters = getEnglishPosters(record.thumbnails);
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {posters.landscape && (
              <img
                src={posters.landscape.file || ''}
                alt="landscape"
                style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px' }}
              />
            )}
            {posters.portrait && (
              <img
                src={posters.portrait.file || ''}
                alt="portrait"
                style={{ width: '35px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
              />
            )}
            {!posters.landscape && !posters.portrait && (
              <div style={{ color: '#999', fontSize: '12px' }}>No poster</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: true,
      render: (_text: string, record: MovieListItem) => {
        const enTitle = record.i18n_title?.en || record.title_en || '';
        const koTitle = record.i18n_title?.ko || '';
        const zhTitle = record.i18n_title?.['zh-hans'] || '';
        return (
          <div
            onClick={() => navigate(`/movie/edit/${record.id}`)}
            style={{ cursor: 'pointer', color: '#1890ff' }}
          >
            {enTitle && <div style={{ fontWeight: 'bold' }}>{enTitle}</div>}
            {(koTitle || zhTitle) && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {koTitle}
                {koTitle && zhTitle && ' / '}
                {zhTitle}
              </div>
            )}
            {!enTitle && !koTitle && !zhTitle && <div style={{ color: '#999' }}>Untitled</div>}
          </div>
        );
      },
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '15%',
      sorter: true,
    },
    // {
    //   title: 'STATUS',
    //   dataIndex: 'status',
    //   key: 'status',
    //   width: '10%',
    //   render: (status: string) => {
    //     const statusMap: Record<string, { color: string; text: string }> = {
    //       published: { color: '#52c41a', text: 'enabled' },
    //       unpublished: { color: '#ff4d4f', text: 'disabled' },
    //     };
    //     const statusInfo = statusMap[status] || { color: '#999', text: status || 'disabled' };
    //     return <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>;
    //   },
    // },
    {
      title: 'ACTIONS',
      dataIndex: 'id',
      key: 'actions',
      width: '10%',
      render: (_: any, record: MovieListItem) => (
        <Space>
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => navigate(`/movie/edit/${record.id}`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              // TODO: Implement delete functionality
              console.log('Delete movie:', record.id);
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Component.BasePageComponent>
      <div className="movie-page">
        {/* Filter Section */}
        <div
          style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }}
        >
          <Space wrap size="middle">
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Service Distribution
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="All Sources"
                value={serviceDistributionFilter}
                onChange={handleServiceDistributionChange}
                options={SERVICE_DISTRIBUTION_OPTIONS}
              />
            </div>
            {/* <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Status</div>
              <Select
                style={{ width: 150 }}
                placeholder="All Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={STATUS_OPTIONS}
              />
            </div> */}
          </Space>
        </div>

        {/* Table */}
        <Component.TableComponent<MovieListItem>
          name="Movie"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          additionalButton={[
            <Button
              key="create-movie"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/movie/edit/new')}
              size="small"
            >
              Create Movie
            </Button>,
            <Button
              key="bulk-update"
              onClick={() => {
                console.log('Bulk update');
              }}
            >
              BULK UPDATE
            </Button>,
          ]}
          columns={columns}
          dataFetch={async (params: URLSearchParams) => {
            const result = await movieService.fetchList(params);

            // Backend already includes thumbnails, no separate call needed
            return {
              data: result.results,
              count: result.count,
            };
          }}
        />
      </div>
    </Component.BasePageComponent>
  );
};

export default Movie;
